import { createApp } from 'vue';
import App from './App.vue';
import './registerServiceWorker';
import router from './router';
import store from './store';
import i18n, { setI18nLocale } from './api/i18n';
import apiClient from './api/index';
import mainPlugins from '@/plugins/mainPlugins.js';
import VueSweetalert2 from 'vue-sweetalert2';
import Swal from 'sweetalert2';
import { onHttpError } from '@/utils/http-error-events';
import 'sweetalert2/dist/sweetalert2.min.css';
const app = createApp(App);
const t = i18n.global.t;

let authErrorPromise = null;
let forbiddenErrorPromise = null;

const handleHttpError = async ({ status, message }) => {
    if (status === 401) {
        if (!authErrorPromise) {
            authErrorPromise = (async () => {
                try {
                    await store.dispatch('auth/signout');

                    const result = await Swal.fire({
                        title: t('django.system_msg__status_code_401'),
                        text:
                            message ||
                            'Authentication has expired. Please sign in again.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });

                    if (
                        result.isConfirmed &&
                        router.currentRoute.value.path !== '/'
                    ) {
                        await router.push('/');
                    }
                } finally {
                    authErrorPromise = null;
                }
            })();
        }

        return authErrorPromise;
    }

    if (status === 403) {
        if (!forbiddenErrorPromise) {
            forbiddenErrorPromise = (async () => {
                try {
                    await Swal.fire({
                        title: t('django.system_msg__status_code_403'),
                        text: 'You do not have permission to access this page',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });

                    if (router.currentRoute.value.path !== '/') {
                        await router.push('/');
                    }
                } finally {
                    forbiddenErrorPromise = null;
                }
            })();
        }

        return forbiddenErrorPromise;
    }

    if (status >= 500) {
        await Swal.fire({
            title: 'Internal server error',
            text: message || 'Please try again later',
            icon: 'error',
            confirmButtonText: 'OK',
        });
        return;
    }

    if (status >= 400 && status < 500) {
        return;
    }

    await Swal.fire({
        title: 'Network error',
        text: 'Unable to reach the server, please try again later.',
        icon: 'error',
        confirmButtonText: 'OK',
    });
};

const loadInitialLanguage = async locale => {
    const response = await apiClient.get(
        `/languages/lang/${locale}?disable_cache=false`,
        { skipAuthorization: true }
    );
    i18n.global.setLocaleMessage(locale, response.data);
    i18n.global.locale.value = locale;
    setI18nLocale(locale, response.data);
    store.commit('i18n/setLocale', { locale, messages: response.data });
};

const initializeApp = async () => {
    const defaultLocale = localStorage.getItem('locale') || 'en';
    try {
        await loadInitialLanguage(defaultLocale);
    } catch (e) {
        console.error(
            'Failed to load initial language, proceeding with fallback.',
            e
        );
    }

    onHttpError(handleHttpError);

    app.use(mainPlugins);
    app.use(store);
    app.use(router);
    app.use(i18n);

    app.use(VueSweetalert2);

    app.mount('#app');
};

initializeApp();
